/**
 * SEOHead Component
 * Dynamically updates meta tags for better SEO
 */

import { useEffect } from 'react';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  canonical?: string;
  noindex?: boolean;
  structuredData?: Record<string, unknown>;
}

const DEFAULT_SEO: SEOProps = {
  title: 'Smart Invoice Generator - Free AI-Powered Invoice Creator',
  description: 'Create professional, customizable invoices in seconds with our free AI-powered invoice generator. Support for multiple currencies, instant PDF export, and intelligent form filling.',
  keywords: 'invoice generator, free invoice maker, AI invoice creator, professional invoice template, PDF invoice',
  ogImage: '/og-image.png',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  canonical: 'https://invoicegenerated.com/',
};

export const SEOHead = ({
  title,
  description,
  keywords,
  ogImage,
  ogType,
  twitterCard,
  canonical,
  noindex = false,
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = (title ?? DEFAULT_SEO.title) as string;

    // Update or create meta tags
    const updateMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Description
    updateMeta('description', description || DEFAULT_SEO.description!);

    // Keywords
    if (keywords) {
      updateMeta('keywords', keywords);
    }

    // Robots
    updateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    updateProperty('og:title', title || DEFAULT_SEO.title!);
    updateProperty('og:description', description || DEFAULT_SEO.description!);
    updateProperty('og:image', ogImage || DEFAULT_SEO.ogImage!);
    updateProperty('og:type', ogType || DEFAULT_SEO.ogType!);

    // Twitter Card
    updateMeta('twitter:card', twitterCard || DEFAULT_SEO.twitterCard!);
    updateMeta('twitter:title', title || DEFAULT_SEO.title!);
    updateMeta('twitter:description', description || DEFAULT_SEO.description!);
    updateMeta('twitter:image', ogImage || DEFAULT_SEO.ogImage!);

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || DEFAULT_SEO.canonical!;

    // Structured Data
    if (structuredData) {
      let script = document.getElementById('structured-data') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = 'structured-data';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Cleanup on unmount
    return () => {
      // Reset to defaults if needed
    };
  }, [title, description, keywords, ogImage, ogType, twitterCard, canonical, noindex, structuredData]);

  return null;
};

export default SEOHead;
